import classNames from 'classnames';
import React, { HTMLAttributes, useRef } from 'react';
import { InputNumber, Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import './index.less';

interface IProps extends HTMLAttributes<HTMLDivElement> {
    id: string;
    width: number;
    height: number;
    disableHeight: boolean;
    disableWidth: boolean;
    lock: boolean;
    lockRatio: boolean;
    onSize?: () => void;
}
const SizeInspector: React.FC<IProps> = ({
    id,
    width,
    height,
    disableHeight,
    disableWidth,
    lock,
    lockRatio,
    onSize,
}) => {
    const { $t } = useLocaleStore();
    const editor = useEditor();
    const isInit = useRef(false);

    const handleSize = (
        key: 'width' | 'height',
        val: number | null,
        options: { writeHistory: boolean; isStart: boolean },
    ) => {
        if (val === null) return;
        if (!lockRatio) return editor.layer.modifyBasicProperties.size(id, { [key]: val, isRatio: false }, options);

        const ratio = width / height;
        const size = key === 'width' ? { width: val, height: val / ratio } : { width: val * ratio, height: val };

        editor.layer.modifyBasicProperties.size(id, { ...size, isRatio: true }, options);
    };

    return (
        <div className="c-base-toolbar-controller">
            <LabelAction title={$t((v) => v.baseToolbar.size)} align>
                <InputNumber
                    v2
                    style={{ width: 73 }}
                    value={width}
                    step={1}
                    min={0}
                    showStepBtn={false}
                    disabled={disableWidth || lock}
                    onChange={(val, isMouseWheel) => {
                        // 输入过程中不实时设置尺寸, 或者会因为最小值限制而出现数字闪烁问题
                        if (!isMouseWheel) return;
                        handleSize('width', val, { writeHistory: false, isStart: !isInit.current });
                        if (!isInit.current) {
                            isInit.current = true;
                        }
                    }}
                    onChangeAfter={(val) => {
                        handleSize('width', val, { writeHistory: true, isStart: false });
                        isInit.current = false;
                        onSize?.();
                    }}
                    suffix={$t((v) => v.baseToolbar.width)}
                />

                <Tooltip
                    placement="bottom"
                    title={lockRatio ? $t((v) => v.baseToolbar.lockRatio) : $t((v) => v.baseToolbar.customRatio)}
                    getPopupContainer={(el) => el.parentNode as HTMLElement}
                >
                    <div
                        className={classNames('c-base-toolbar-controller__icon', { locked: lockRatio })}
                        onClick={() => {
                            if (lock) return;
                            editor.layer.modifyBasicProperties.stretch(id, !lockRatio);
                        }}
                    >
                        <IconFont
                            className={classNames({ disabled: lock })}
                            name={lockRatio ? 'LockBold' : 'UnlockBold'}
                            fontSize={16}
                        />
                    </div>
                </Tooltip>

                <InputNumber
                    v2
                    style={{ width: 73 }}
                    value={height}
                    step={1}
                    min={0}
                    showStepBtn={false}
                    disabled={disableHeight || lock}
                    onChange={(val, isMouseWheel) => {
                        // 输入过程中不实时设置尺寸, 或者会因为最小值限制而出现数字闪烁问题
                        if (!isMouseWheel) return;
                        handleSize('height', val, { writeHistory: false, isStart: !isInit.current });
                        if (!isInit.current) {
                            isInit.current = true;
                        }
                    }}
                    onChangeAfter={(val) => {
                        handleSize('height', val, { writeHistory: true, isStart: false });
                        isInit.current = false;
                        onSize?.();
                    }}
                    suffix={$t((v) => v.baseToolbar.height)}
                />
            </LabelAction>
        </div>
    );
};
export default React.memo(SizeInspector);
